/**
 * Switches desktop inline nav to hamburger when menu items would wrap.
 */
class HeaderNavOverflow {
  constructor(header) {
    this.header = header;
    this.inlineMenu = header.querySelector('.header__inline-menu');
    this.menuList = this.inlineMenu?.querySelector('.list-menu--inline');
    this.heading = header.querySelector('.header__heading-link, .header__heading');
    this.icons = header.querySelector('.header__icons');
    this.onResize = this.scheduleCheck.bind(this);
    this.onBreakpointChange = this.handleBreakpointChange.bind(this);
    this.rafId = null;
    this.desktopMql = window.matchMedia('(min-width: 990px)');

    if (!this.menuList || !this.heading || !this.icons) return;

    this.header.dataset.navOverflowInit = 'true';
    this.observer = new ResizeObserver(this.onResize);
    this.observer.observe(this.header);
    this.observer.observe(this.heading);
    this.observer.observe(this.icons);
    this.observer.observe(this.menuList);

    this.buffer = 8;

    if (document.fonts?.ready) {
      document.fonts.ready.then(this.onResize);
    }

    window.addEventListener('resize', this.onResize, { passive: true });
    this.desktopMql.addEventListener('change', this.onBreakpointChange);
    this.scheduleCheck();
  }

  handleBreakpointChange(event) {
    this.closeMobileDrawer();

    if (!event.matches) {
      this.header.classList.remove('header--nav-compact');
      return;
    }

    this.check();
  }

  scheduleCheck() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.check();
    });
  }

  measureMenuWidth() {
    const inlineMenu = this.inlineMenu;
    const previous = {
      display: inlineMenu.style.display,
      visibility: inlineMenu.style.visibility,
      position: inlineMenu.style.position,
      pointerEvents: inlineMenu.style.pointerEvents,
      left: inlineMenu.style.left,
      top: inlineMenu.style.top,
      width: inlineMenu.style.width,
    };

    inlineMenu.style.setProperty('display', 'block', 'important');
    inlineMenu.style.visibility = 'hidden';
    inlineMenu.style.position = 'absolute';
    inlineMenu.style.pointerEvents = 'none';
    inlineMenu.style.left = '-9999px';
    inlineMenu.style.top = '0';
    inlineMenu.style.width = 'max-content';

    const width = this.menuList.scrollWidth;

    this.restoreInlineStyle(inlineMenu, 'display', previous.display);
    this.restoreInlineStyle(inlineMenu, 'visibility', previous.visibility);
    this.restoreInlineStyle(inlineMenu, 'position', previous.position);
    this.restoreInlineStyle(inlineMenu, 'pointerEvents', previous.pointerEvents);
    this.restoreInlineStyle(inlineMenu, 'left', previous.left);
    this.restoreInlineStyle(inlineMenu, 'top', previous.top);
    this.restoreInlineStyle(inlineMenu, 'width', previous.width);

    return width;
  }

  restoreInlineStyle(element, property, value) {
    if (value) {
      element.style[property] = value;
      return;
    }

    const cssProperty = property.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
    element.style.removeProperty(cssProperty);
  }

  getLogoRight() {
    const logoEl =
      this.header.querySelector('.header__heading-logo-wrapper') ||
      this.header.querySelector('.header__heading-link');
    return logoEl.getBoundingClientRect().right;
  }

  getAvailableWidth() {
    const gap = parseFloat(getComputedStyle(this.header).columnGap) || 0;
    return this.icons.getBoundingClientRect().left - this.getLogoRight() - gap;
  }

  isNavColliding() {
    const menuRect = this.menuList.getBoundingClientRect();
    const iconsRect = this.icons.getBoundingClientRect();
    return menuRect.right > iconsRect.left - this.buffer;
  }

  isHeaderOverflowing() {
    return this.header.getBoundingClientRect().right > document.documentElement.clientWidth;
  }

  needsCompactInCurrentLayout() {
    const menuWidth = this.measureMenuWidth();
    const available = this.getAvailableWidth();

    return (
      this.isNavColliding() ||
      this.isHeaderOverflowing() ||
      menuWidth > available - this.buffer
    );
  }

  /**
   * While in compact mode, icons sit in a flex row and under-report how much
   * space the inline nav would get in the expanded grid. Temporarily switch to
   * the expanded layout, measure, then restore — all synchronously so nothing
   * paints between states.
   */
  fitsInExpandedLayout() {
    const wasCompact = this.header.classList.contains('header--nav-compact');
    if (!wasCompact) {
      return !this.needsCompactInCurrentLayout();
    }

    this.header.classList.remove('header--nav-compact');
    void this.header.offsetHeight;

    const fits = !this.needsCompactInCurrentLayout();

    if (!fits) {
      this.header.classList.add('header--nav-compact');
    }

    return fits;
  }

  closeDesktopDropdowns() {
    this.header.querySelectorAll('header-menu details[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  }

  closeMobileDrawer() {
    const drawer = this.header.querySelector('header-drawer');
    const details = drawer?.querySelector('details');
    if (!details?.hasAttribute('open')) return;

    const summary = details.querySelector('summary');
    if (typeof drawer.closeMenuDrawer === 'function' && summary) {
      drawer.closeMenuDrawer(new Event('resize'), summary);
      return;
    }

    details.removeAttribute('open');
    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach((submenu) => {
      submenu.removeAttribute('open');
      submenu.classList.remove('menu-opening');
    });
    this.header.classList.remove('menu-open');
    document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop');
  }

  check() {
    if (!this.desktopMql.matches) {
      this.header.classList.remove('header--nav-compact');
      return;
    }

    if (this.header.classList.contains('header--nav-compact')) {
      if (!this.fitsInExpandedLayout()) {
        this.closeDesktopDropdowns();
      }
      return;
    }

    if (this.needsCompactInCurrentLayout()) {
      this.header.classList.add('header--nav-compact');
      this.closeDesktopDropdowns();
    }
  }
}

function initHeaderNavOverflow(root = document) {
  root.querySelectorAll('.header.header--has-menu').forEach((header) => {
    if (header.dataset.navOverflowInit === 'true') return;
    new HeaderNavOverflow(header);
  });
}

initHeaderNavOverflow();

document.addEventListener('shopify:section:load', (event) => {
  initHeaderNavOverflow(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('.header.header--has-menu').forEach((header) => {
    delete header.dataset.navOverflowInit;
    header.classList.remove('header--nav-compact');
  });
});
